import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Search,
  Package,
  AlertCircle,
  X
} from 'lucide-react'
import { Purchase, Supplier, Product, PurchaseItem } from '../types/api'
import { apiClient } from '../services/api'

interface LineItem extends Omit<PurchaseItem, 'id' | 'purchase_id' | 'created_at' | 'updated_at'> {
  tempId?: string
}

export const PurchaseForm = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [productSearch, setProductSearch] = useState('')
  const [searchingProducts, setSearchingProducts] = useState(false)
  const [showCustomProductModal, setShowCustomProductModal] = useState(false)

  const [formData, setFormData] = useState({
    supplier_id: '',
    purchase_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    payment_method: 'credit' as const,
    payment_terms: '',
    status: 'draft' as const,
    notes: '',
    shipping_cost: 0,
    discount_amount: 0
  })

  const [lineItems, setLineItems] = useState<LineItem[]>([])

  useEffect(() => {
    loadSuppliers()
    if (id && isEditMode) {
      loadPurchaseData()
    }
  }, [id, isEditMode])

  const loadSuppliers = async () => {
    try {
      const response = await apiClient.getSuppliers({ limit: 1000 })
      setSuppliers(response.suppliers || [])
    } catch (err) {
      console.error('Error loading suppliers:', err)
    }
  }

  const loadProducts = async (search?: string) => {
    try {
      setSearchingProducts(true)
      const response = await apiClient.getProducts({
        limit: 100,
        search: search || undefined
      })
      setProducts(response.products || [])
    } catch (err) {
      console.error('Error loading products:', err)
    } finally {
      setSearchingProducts(false)
    }
  }

  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch.length >= 2) {
        loadProducts(productSearch)
      } else if (productSearch.length === 0) {
        setProducts([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [productSearch])

  const loadPurchaseData = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getPurchase(id!)
      const purchase = response.purchase

      setFormData({
        supplier_id: purchase.supplier_id || '',
        purchase_date: purchase.purchase_date,
        expected_delivery_date: purchase.expected_delivery_date || '',
        payment_method: purchase.payment_method,
        payment_terms: purchase.payment_terms || '',
        status: purchase.status,
        notes: purchase.notes || '',
        shipping_cost: Number(purchase.shipping_cost) || 0,
        discount_amount: Number(purchase.discount_amount) || 0
      })

      if (purchase.items) {
        setLineItems(purchase.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          unit: item.unit,
          quantity_ordered: item.quantity_ordered,
          quantity_received: item.quantity_received,
          unit_price: Number(item.unit_price),
          tax_rate: Number(item.tax_rate) || 0,
          tax_amount: Number(item.tax_amount) || 0,
          discount_percent: Number(item.discount_percent) || 0,
          discount_amount: Number(item.discount_amount) || 0,
          total_price: Number(item.total_price),
          notes: item.notes
        })))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load purchase')
    } finally {
      setLoading(false)
    }
  }

  const addLineItem = (product: Product) => {
    const newItem: LineItem = {
      tempId: Date.now().toString(),
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku || '',
      unit: product.unit || 'pcs',
      quantity_ordered: 1,
      unit_price: Number(product.cost_price) || Number(product.price) || 0,
      tax_rate: Number(product.gst_rate) || 0,
      tax_amount: 0,
      discount_percent: 0,
      discount_amount: 0,
      total_price: 0
    }

    // Calculate initial totals
    const updated = calculateLineItemTotals(newItem)
    setLineItems([...lineItems, updated])

    // Clear search to allow adding more products
    setProductSearch('')
  }

  const calculateLineItemTotals = (item: LineItem): LineItem => {
    const baseAmount = item.quantity_ordered * item.unit_price
    const discountAmount = item.discount_percent > 0
      ? (baseAmount * item.discount_percent) / 100
      : item.discount_amount || 0

    const afterDiscount = baseAmount - discountAmount
    const taxAmount = (afterDiscount * item.tax_rate) / 100
    const totalPrice = afterDiscount + taxAmount

    return {
      ...item,
      discount_amount: discountAmount,
      tax_amount: taxAmount,
      total_price: totalPrice
    }
  }

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const updated = [...lineItems]
    updated[index] = { ...updated[index], [field]: value }

    // Recalculate totals
    updated[index] = calculateLineItemTotals(updated[index])
    setLineItems(updated)
  }

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index))
  }

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity_ordered * item.unit_price), 0)
    const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0)
    const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0)
    const totalAmount = subtotal - totalDiscount - (formData.discount_amount || 0) + totalTax + (formData.shipping_cost || 0)

    return {
      subtotal,
      totalDiscount,
      totalTax,
      totalAmount
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.supplier_id) {
      setError('Please select a supplier')
      return
    }

    if (lineItems.length === 0) {
      setError('Please add at least one item')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const totals = calculateTotals()

      const purchaseData = {
        ...formData,
        subtotal: totals.subtotal,
        tax_amount: totals.totalTax,
        discount_amount: formData.discount_amount || 0,
        shipping_cost: formData.shipping_cost || 0,
        total_amount: totals.totalAmount,
        items: lineItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          unit: item.unit,
          quantity_ordered: item.quantity_ordered,
          quantity_received: item.quantity_received || 0,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate || 0,
          tax_amount: item.tax_amount || 0,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          total_price: item.total_price,
          notes: item.notes
        }))
      }

      if (isEditMode && id) {
        await apiClient.updatePurchase(id, purchaseData)
      } else {
        await apiClient.createPurchase(purchaseData)
      }

      navigate('/purchases')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save purchase')
    } finally {
      setLoading(false)
    }
  }

  const totals = calculateTotals()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/purchases')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Purchases
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditMode ? 'Edit Purchase Order' : 'New Purchase Order'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Purchase Details */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Purchase Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Supplier *
              </label>
              <select
                value={formData.supplier_id}
                onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="">Select Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Purchase Date *
              </label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expected Delivery
              </label>
              <input
                type="date"
                value={formData.expected_delivery_date}
                onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Payment Method
              </label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="credit">Credit</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="upi">UPI</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Payment Terms
              </label>
              <input
                type="text"
                value={formData.payment_terms}
                onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Net 30"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="draft">Draft</option>
                <option value="ordered">Ordered</option>
                <option value="partially_received">Partially Received</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Items</h2>

          {/* Product Search with Autocomplete - Always Visible */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products by name, SKU, or barcode..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {searchingProducts && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>

              {/* Add Custom Product Button */}
              <button
                type="button"
                onClick={() => setShowCustomProductModal(true)}
                className="inline-flex items-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap font-medium"
                title="Add custom product"
              >
                <Plus className="w-5 h-5" />
                Add Custom
              </button>
            </div>

            {/* Product Results Dropdown */}
            {productSearch.length >= 2 && (
              <div className="relative">
                <div className="absolute z-10 w-full mt-1 max-h-80 overflow-y-auto border-2 border-blue-200 dark:border-blue-800 rounded-lg bg-white dark:bg-gray-800 shadow-xl">
                  {searchingProducts ? (
                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      Searching products...
                    </div>
                  ) : products.length === 0 ? (
                    <div className="p-6 text-center">
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No products found</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try a different search term</p>
                    </div>
                  ) : (
                    products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => addLineItem(product)}
                        className="w-full p-4 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {product.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3">
                              {product.sku && (
                                <span className="flex items-center gap-1">
                                  <span className="font-medium">SKU:</span> {product.sku}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <span className="font-medium">Stock:</span> {product.stock_quantity} {product.unit}
                              </span>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                              {formatCurrency(Number(product.cost_price || product.price))}
                            </div>
                            <button className="mt-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 rounded text-sm font-medium group-hover:bg-blue-600 group-hover:text-white transition-colors">
                              Add
                            </button>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {productSearch.length > 0 && productSearch.length < 2 && (
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300 text-center">
                  Type at least 2 characters to search products...
                </p>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Product</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Qty</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Unit Price</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Tax %</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Discount %</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Total</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {lineItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No items added yet
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item, index) => (
                    <tr key={item.tempId || index}>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.product_name}</td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={item.quantity_ordered}
                          onChange={(e) => updateLineItem(index, 'quantity_ordered', Number(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="0.01"
                          step="0.01"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', Number(e.target.value))}
                          className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={item.tax_rate || 0}
                          onChange={(e) => updateLineItem(index, 'tax_rate', Number(e.target.value))}
                          className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={item.discount_percent || 0}
                          onChange={(e) => updateLineItem(index, 'discount_percent', Number(e.target.value))}
                          className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">
                        ₹{item.total_price.toFixed(2)}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals and Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h2>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Add any notes or special instructions..."
            />
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="font-medium text-gray-900 dark:text-white">₹{totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Tax:</span>
                <span className="font-medium text-gray-900 dark:text-white">₹{totals.totalTax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">Additional Discount:</span>
                <input
                  type="number"
                  value={formData.discount_amount}
                  onChange={(e) => setFormData({ ...formData, discount_amount: Number(e.target.value) })}
                  className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">Shipping:</span>
                <input
                  type="number"
                  value={formData.shipping_cost}
                  onChange={(e) => setFormData({ ...formData, shipping_cost: Number(e.target.value) })}
                  className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-900 dark:text-white">Total:</span>
                  <span className="text-gray-900 dark:text-white">₹{totals.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate('/purchases')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : isEditMode ? 'Update Purchase' : 'Create Purchase'}
          </button>
        </div>
      </form>

      {/* Custom Product Modal */}
      {showCustomProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Custom Product</h3>
              <button
                type="button"
                onClick={() => setShowCustomProductModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter product name"
                  id="custom-product-name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    placeholder="1"
                    min="0.01"
                    step="0.01"
                    defaultValue="1"
                    id="custom-product-quantity"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unit Price *
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    id="custom-product-price"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    defaultValue="0"
                    id="custom-product-tax"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Discount (%)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    defaultValue="0"
                    id="custom-product-discount"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCustomProductModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const name = (document.getElementById('custom-product-name') as HTMLInputElement)?.value
                    const quantity = parseFloat((document.getElementById('custom-product-quantity') as HTMLInputElement)?.value || '1')
                    const price = parseFloat((document.getElementById('custom-product-price') as HTMLInputElement)?.value || '0')
                    const taxRate = parseFloat((document.getElementById('custom-product-tax') as HTMLInputElement)?.value || '0')
                    const discountPercent = parseFloat((document.getElementById('custom-product-discount') as HTMLInputElement)?.value || '0')

                    if (!name || !price) {
                      setError('Product name and price are required')
                      return
                    }

                    // Create custom line item
                    const newItem: LineItem = {
                      tempId: Date.now().toString(),
                      product_id: `custom-${Date.now()}`,
                      product_name: name,
                      product_sku: '',
                      unit: 'pcs',
                      quantity_ordered: quantity,
                      unit_price: price,
                      tax_rate: taxRate,
                      tax_amount: 0,
                      discount_percent: discountPercent,
                      discount_amount: 0,
                      total_price: 0
                    }

                    // Calculate totals
                    const updated = calculateLineItemTotals(newItem)
                    setLineItems([...lineItems, updated])

                    setShowCustomProductModal(false)
                    setError(null)
                  }}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  Add Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
